/**
 * New Dashboard prototype — `/dashboard-mock`.
 *
 * Self-contained clickable mock of the Figma "New Dashboard" frames. Mounted at
 * the top level of `src/routes/` (NOT under `_app/`) so the authenticated shell
 * does not wrap it and no Firebase auth is required. Driven entirely by local
 * mock data; see `src/features/dashboard-mock/`.
 *
 * Dev-only (FE-ISSUE 15.1) — the same two-layer gate as `/dev-sim`:
 *   1. Runtime inertness — `beforeLoad` throws `notFound()` unless
 *      `isDashboardMockRouteEnabled()` (non-production builds only).
 *   2. Tree-shaking — production builds collapse the component to a
 *      `() => null` stub so Rollup drops the dynamic
 *      `import('@/features/dashboard-mock/DashboardMockApp')` chunk entirely.
 *      (`frames.ts` stays an eager import: it is a zero-dependency leaf.)
 *
 * URL state: `?frame=<main|first-time>`. With no param, a launcher lists the
 * page states.
 */
import { createFileRoute, notFound } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'

import { isDashboardMockRouteEnabled } from '@/lib/dashboard-mock-access'
import { isFrameId, type FrameId } from '@/features/dashboard-mock/frames'

const shouldBundleDashboardMock = !import.meta.env.PROD

const DashboardMockApp = !shouldBundleDashboardMock
  ? () => null
  : lazy(() =>
      import('@/features/dashboard-mock/DashboardMockApp').then((m) => ({
        default: m.DashboardMockApp,
      })),
    )

function DashboardMockRoute() {
  return (
    <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading prototype…</div>}>
      <DashboardMockApp />
    </Suspense>
  )
}

interface DashboardMockSearch {
  frame?: FrameId
}

export const Route = createFileRoute('/dashboard-mock')({
  beforeLoad: () => {
    if (!isDashboardMockRouteEnabled()) throw notFound()
  },
  validateSearch: (search: Record<string, unknown>): DashboardMockSearch => {
    const raw = search.frame == null ? undefined : String(search.frame)
    return { frame: isFrameId(raw) ? raw : undefined }
  },
  component: DashboardMockRoute,
})
